import { $$ } from '@utils';

/* ──────────────────────────────────────────────
 * Estado global del módulo (solo uno activo)
 * ────────────────────────────────────────────── */
let activePlayer = null;

/* ──────────────────────────────────────────────
 * Init
 * ────────────────────────────────────────────── */
function initWithin(root = document) {
  const players = root.querySelectorAll
    ? root.querySelectorAll('[data-au-audio-player]:not([data-au-audio-player-init])')
    : $$('[data-au-audio-player]:not([data-au-audio-player-init])');

  if (!players.length) return;

  players.forEach((player) => {
    player.setAttribute('data-au-audio-player-init', 'true');
    initAudio(player);
  });
}

function init() {
  initWithin(document);
}

/* ──────────────────────────────────────────────
 * Status Helpers
 * ────────────────────────────────────────────── */
function setActivePlayer(player) {
  activePlayer = player;
}

function clearActivePlayerIfCurrent(audio) {
  if (activePlayer?.audio === audio) {
    activePlayer = null;
  }
}

function setPlayingUI(playBtn) {
  const iconBtn = playBtn.querySelector('[data-au-icon]');
  iconBtn.textContent = 'pause';
}

function setPausedUI(playBtn) {
  const iconBtn = playBtn.querySelector('[data-au-icon]');
  iconBtn.textContent = 'play_arrow';
}

function formatTime(totalSeconds = 0) {
  const t = Math.max(0, Math.floor(totalSeconds));
  const sec = String(t % 60).padStart(2, '0');
  const min = String(Math.floor(t / 60)).padStart(2, '0');
  return `${min}:${sec}`;
}

function getInitialLabel(player, audio) {
  return player.dataset.auAudioInitialTime || (audio?.duration ? formatTime(audio.duration) : '00:00');
}

function getAudioTitle(player, audio) {
  return audio?.getAttribute('title')?.trim() || player?.dataset?.auAudioTitle || '';
}
function getProgressBucket(audio) {
  const d = audio?.duration || 0;
  const t = audio?.currentTime || 0;
  if (!d) return 0;

  const pct = (t / d) * 100;
  const bucket = Math.min(100, Math.max(0, Math.floor(pct / 25) * 25)); // 0,25,50,75,100
  return bucket;
}

function pushAudioProgressOnPause(player, audio) {
  const audioTitle = getAudioTitle(player, audio);
  if (!audioTitle) return;

  const bucket = getProgressBucket(audio);

  if (audio.__lastProgressBucket === bucket) return;
  audio.__lastProgressBucket = bucket;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'navigation',
    content_type: 'clickBoton',
    item_id: `${audioTitle}_${bucket}`,
  });
}

/* ──────────────────────────────────────────────
 * Player
 * ────────────────────────────────────────────── */
function initAudio(player) {
  let audioBuffer = null;
  let lastBarCount = 0;
  const audio = player.querySelector('[data-au-audio-player-audio]');
  const wave = player.querySelector('[data-au-audio-player-wave]');
  const playBtn = player.querySelector('[data-au-audio-player-play]');
  const timeLabel = player.querySelector('[data-au-audio-player-time]');
  const isPreview = player.hasAttribute('data-au-audio-player-preview');

  if (!audio || !wave || !playBtn || !timeLabel) return;

  let bars = [];
  let audioCtx;

  const MAX_BAR_HEIGHT = 32;
  const MIN_BAR_HEIGHT = 4;
  const BAR_WIDTH = 4;
  const BAR_GAP = 4;

  function maxBarsThatFit() {
    const w = wave.clientWidth || 0;
    return Math.max(1, Math.floor((w + BAR_GAP) / (BAR_WIDTH + BAR_GAP)));
  }

  function renderBars(barCount) {
    const waveformData = getWaveformData(audioBuffer, barCount);

    wave.innerHTML = '';
    bars = [];

    for (let i = 0; i < barCount; i++) {
      const bar = document.createElement('div');
      bar.classList.add('au-audio-player__wave-bar');

      const { min, max } = waveformData[i];
      const amp = Math.max(Math.abs(min), Math.abs(max));
      const h = MIN_BAR_HEIGHT + amp * (MAX_BAR_HEIGHT - MIN_BAR_HEIGHT);

      bar.style.height = `${h}px`;
      wave.appendChild(bar);
      bars.push(bar);
    }

    // repaint current progress after re-rendering
    if (audio.duration) {
      const progress = audio.currentTime / audio.duration;
      const playedBars = Math.floor(progress * bars.length);
      bars.forEach((bar, idx) => bar.classList.toggle('au-audio-player__wave-played', idx < playedBars));
    }
  }

  function updateWaveToFit() {
    if (!audioBuffer) return;

    const count = maxBarsThatFit();
    if (count === lastBarCount) return;

    lastBarCount = count;
    renderBars(count);
  }

  async function createBarsFromAudio() {
    const ctx = getAudioContext();
    const response = await fetch(audio.dataset.auAudioPlayerAudio);
    const arrayBuffer = await response.arrayBuffer();
    audioBuffer = await ctx.decodeAudioData(arrayBuffer);
    const initial = formatTime(audioBuffer.duration || 0);
    player.dataset.auAudioInitialTime = initial;
    timeLabel.textContent = initial;

    updateWaveToFit();
  }

  function getAudioContext() {
    if (!audioCtx) {
      audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    }
    return audioCtx;
  }

  function getWaveformData(buffer, barCount) {
    const raw = buffer.getChannelData(0);
    const samplesPerBar = Math.floor(raw.length / barCount) || 1;
    const data = [];

    for (let i = 0; i < barCount; i++) {
      const start = i * samplesPerBar;
      const end = Math.min(start + samplesPerBar, raw.length);
      let min = 1.0;
      let max = -1.0;

      for (let j = start; j < end; j++) {
        const sample = raw[j];
        if (sample < min) min = sample;
        if (sample > max) max = sample;
      }

      data.push({ min, max });
    }

    return data;
  }
  if (isPreview) {
    playBtn.addEventListener('click', (e) => {
      e.preventDefault();
    });
    wave.addEventListener('click', (e) => {
      e.preventDefault();
    });
  }

  /* ───────────────
   * Play / Pause
   * ─────────────── */
  playBtn.addEventListener('click', async () => {
    if (activePlayer && activePlayer.audio !== audio) {
      activePlayer.audio.pause();
      setPausedUI(activePlayer.playBtn);
    }
    const audioTitle = getAudioTitle(player, audio);
    if (audio.paused) {
      // Load src dynamically if not already loaded
      if (!audio.src) {
        const audioSrc = audio.getAttribute('data-au-audio-player-audio');
        if (audioSrc) {
          audio.src = audioSrc;
        }
      }
      getAudioContext().resume();
      setActivePlayer({ audio, playBtn });
      setPlayingUI(playBtn);
      await audio.play();
      window.dataLayer.push({
        event: 'navigation',
        content_type: 'clickBoton',
        item_id: `${audioTitle}_play`,
      });
    } else {
      audio.pause();
      setPausedUI(playBtn);
      clearActivePlayerIfCurrent(audio);
      pushAudioProgressOnPause(player, audio);
      window.dataLayer.push({
        event: 'navigation',
        content_type: 'clickBoton',
        item_id: `${audioTitle}_pause`,
      });
    }
  });

  audio.addEventListener('ended', () => {
    setPausedUI(playBtn);
    clearActivePlayerIfCurrent(audio);
    audio.currentTime = 0;
    timeLabel.textContent = getInitialLabel(player, audio);
    window.dataLayer = window.dataLayer || [];
    const audioTitle = getAudioTitle(player, audio);
    window.dataLayer.push({
      event: 'navigation',
      content_type: 'clickBoton',
      item_id: `${audioTitle}_100`,
    });
  });

  /* ───────────────
   * Time / Progress
   * ─────────────── */
  audio.addEventListener('timeupdate', () => {
    if (!audio.duration) return;

    if (audio.paused && audio.currentTime === 0) {
      timeLabel.textContent = getInitialLabel(player, audio);
      return;
    }

    const progress = audio.currentTime / audio.duration;
    const t = Math.floor(audio.currentTime);

    const sec = String(t % 60).padStart(2, '0');
    const min = String(Math.floor(t / 60)).padStart(2, '0');
    timeLabel.textContent = `${min}:${sec}`;

    const playedBars = Math.floor(progress * bars.length);

    bars.forEach((bar, index) => {
      bar.classList.toggle('au-audio-player__wave-played', index < playedBars);
    });
  });

  audio.addEventListener('loadedmetadata', () => {
    const initial = formatTime(audio.duration || 0);
    player.dataset.auAudioInitialTime = initial;
    timeLabel.textContent = initial;
  });

  audio.addEventListener('play', () => setPlayingUI(playBtn));
  audio.addEventListener('pause', () => {
    setPausedUI(playBtn);
    clearActivePlayerIfCurrent(audio);

    if (audio.currentTime === 0) {
      timeLabel.textContent = getInitialLabel(player, audio);
    }
  });

  /* ───────────────
   * Click on the wave
   * ─────────────── */
  wave.addEventListener('click', (e) => {
    if (!audio.duration) return;

    const rect = wave.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const ratio = Math.min(1, Math.max(0, x / rect.width));

    audio.currentTime = ratio * audio.duration;

    if (activePlayer && activePlayer.audio !== audio) {
      activePlayer.audio.pause();
      setPausedUI(activePlayer.playBtn);
    }

    // Cargar src dinámicamente si no está cargado
    if (!audio.src) {
      const audioSrc = audio.getAttribute('data-au-audio-player-audio');
      if (audioSrc) {
        audio.src = audioSrc;
      }
    }

    getAudioContext().resume();
    setActivePlayer({ audio, playBtn });
    setPlayingUI(playBtn);
    audio.play();
  });

  /* ───────────────
   * Init waveform
   * ─────────────── */
  createBarsFromAudio().catch((err) => {
    console.error('Error al crear la forma de onda:', err);
  });

  const ro = new ResizeObserver(() => {
    updateWaveToFit();
  });
  ro.observe(wave);
}

/* ───────────────
 * Reset audio player
 * ─────────────── */
function resetPlayer(player) {
  const audio = player.querySelector('[data-au-audio-player-audio]');
  const playBtn = player.querySelector('[data-au-audio-player-play]');
  const iconBtn = playBtn.querySelector('[data-au-icon]');
  const timeLabel = player.querySelector('[data-au-audio-player-time]');
  const wave = player.querySelector('[data-au-audio-player-wave]');

  if (audio) {
    audio.pause();
    audio.currentTime = 0;
  }

  if (playBtn) iconBtn.textContent = 'play_arrow';
  if (timeLabel) {
    const initial = player.dataset.auAudioInitialTime || (audio?.duration ? formatTime(audio.duration) : '00:00');
    timeLabel.textContent = initial;
  }

  if (wave) {
    wave.querySelectorAll('.bar.played').forEach((bar) => bar.classList.remove('played'));
  }
}

function resetWithin(root = document) {
  const players = root.querySelectorAll('[data-au-audio-player]');
  players.forEach(resetPlayer);
}
export default { init, initWithin, resetWithin };
